import React, { useState } from 'react';
import { Button, Dimmer, Loader } from 'semantic-ui-react';
import { addResidentAnswer } from '../../services/Survey';
import { FacilityQuestion } from '../../types/Survey';
import sendToast from '../../../utils/Toast';
import { SurveyIcon } from '../icons';
import './style.less';

/* Layout-only Tailwind (spacing/flex structure); colors, gradients, shadows and
   animations stay in style.less scoped under .neighbors-survey. Mirrors the
   Links page shell/hero so the two pages read as one family. */
const SURVEY_SHELL =
    'neighbors-survey box-border relative mx-auto min-w-0 w-full max-w-survey-page flex-1 px-nbp-16 pt-nbp-20 pb-nbp-32 sm:px-nbp-24 sm:pt-nbp-24 sm:pb-nbp-40 md:px-nbp-36 md:pt-nbp-36 md:pb-nbp-64';
const SURVEY_HERO = 'survey-hero min-w-0 w-full p-nbp-20 sm:p-nbp-24 md:px-nbp-40 md:py-nbp-36';
const SURVEY_CARD = 'survey-card flex min-w-0 w-full flex-col gap-nbp-16 p-nbp-16 sm:p-nbp-20 md:p-nbp-24';
const SURVEY_EMPTY = 'survey-empty-state min-w-0 w-full px-nbp-16 py-nbp-32 sm:px-nbp-20 sm:py-nbp-48';

interface SurveyQuestionsProps {
    questions: FacilityQuestion[];
    loading: boolean;
    facilityId: string;
    residentId: string;
}

const ALREADY_ANSWERED_MESSAGE = 'You have already answered this question';

const SurveyQuestions = ({ questions, loading, facilityId, residentId }: SurveyQuestionsProps) => {
    const [responses, setResponses] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState<string | null>(null);
    const [answered, setAnswered] = useState<Record<string, boolean>>({});

    const handleSubmit = async (question: string) => {
        if (submitting || answered[question]) return;
        const response = responses[question]?.trim();
        if (!response) {
            sendToast('warn', 'Please enter your answer');
            return;
        }
        setSubmitting(question);
        try {
            await addResidentAnswer(facilityId, { question, residentId, response });
            sendToast('success', 'Answer submitted');
            setAnswered((prev) => ({ ...prev, [question]: true }));
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to submit answer';
            if (message.includes(ALREADY_ANSWERED_MESSAGE)) {
                setAnswered((prev) => ({ ...prev, [question]: true }));
            }
            sendToast('error', message);
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <div className={SURVEY_SHELL}>
            <Dimmer active={loading} inverted>
                <Loader content="Loading survey" />
            </Dimmer>

            <div className={SURVEY_HERO}>
                <div className="survey-hero-eyebrow">Your Feedback</div>
                <div className="survey-hero-title text-nbp-22 sm:text-nbp-24 md:text-nbp-28">Survey</div>
                <div className="survey-hero-sub">Share your thoughts with your community team</div>
            </div>

            {questions.length > 0 ? (
                <div className="survey-list flex min-w-0 w-full flex-col gap-nbp-16">
                    {questions.map((q, index) => {
                        const textareaId = `survey-answer-${index}`;
                        const isSubmitting = submitting === q.question;
                        const isAnswered = !!answered[q.question];
                        return (
                            <div key={q._id ?? `${q.question}-${index}`} className={SURVEY_CARD}>
                                <div className="flex min-w-0 items-start gap-nbp-12">
                                    <span className="survey-question-num h-nbp-28 w-nbp-28">{index + 1}</span>
                                    <label htmlFor={textareaId} className="survey-question min-w-0">
                                        {q.question}
                                    </label>
                                </div>
                                <textarea
                                    id={textareaId}
                                    className="survey-textarea"
                                    placeholder="Write your answer here..."
                                    value={responses[q.question] ?? ''}
                                    onChange={(event) => {
                                        const { value } = event.target;
                                        setResponses((prev) => ({ ...prev, [q.question]: value }));
                                    }}
                                    rows={4}
                                    disabled={isAnswered}
                                />
                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        onClick={() => handleSubmit(q.question)}
                                        loading={isSubmitting}
                                        disabled={loading || isSubmitting || isAnswered}
                                        className="survey-submit-btn"
                                    >
                                        {isAnswered ? 'Answered' : 'Answer'}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                !loading && (
                    <div className={SURVEY_EMPTY}>
                        <div className="survey-empty-icon">
                            <SurveyIcon size={48} />
                        </div>
                        <div className="survey-empty-title">No survey questions</div>
                        <div className="survey-empty-sub">There are no survey questions at this time.</div>
                    </div>
                )
            )}
        </div>
    );
};

export default SurveyQuestions;
